import PropTypes from 'prop-types';
import { useMemo, useEffect } from 'react';
import { useMatrixClient } from '../../../../../context/MatrixContext';
import { useI18n } from '../../../../../context/I18nContext';
import { EMOJI_PICKER_DIMENSIONS } from '../../../../../constants';
import { emojiToUnified } from '../../../../../utils/emoji';
import EmojiPickerReact, { Categories } from 'emoji-picker-react';
import './SmartEmojiPicker.css';

const SmartEmojiPicker = ({
  messageId,
  reactions = new Map(), // All reactions for this message
  onToggleReaction,
  onClose = () => {},
  onExpand = () => {},
}) => {
  const client = useMatrixClient();
  const { t } = useI18n();
  const currentUserId = client?.getUserId();

  // Get current user's reactions for highlighting
  const currentUserReactions = useMemo(() => {
    const userReactions = new Map();
    
    reactions.forEach((reactors, emoji) => {
      const userReaction = reactors.find(reactor => reactor.senderId === currentUserId);
      if (userReaction) {
        userReactions.set(emoji, userReaction);
      }
    });
    
    return userReactions;
  }, [reactions, currentUserId]);

  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
  
    // Check if user has already reacted with this emoji
    const existingReaction = currentUserReactions.get(emoji);
    
    if (existingReaction) {
      // User has already reacted - toggle it off (remove)
      onToggleReaction(messageId, emoji, existingReaction.eventId);
    } else {
      // User hasn't reacted - add new reaction
      onToggleReaction(messageId, emoji, null);
    }
    
    onClose();
  };

  // Our custom reactions - simple array of unified emoji ids
  // Include user's current reaction at the end if they've reacted
  const quickReactions = useMemo(() => {
    const baseReactions = [
      '👍', // thumbs up
      '👎', // thumbs down  
      '🙌', // raised hands
      '👏', // clapping
      '🤝', // handshake
      '😊', // smile
    ];

    // Get the user's current reaction (there should only be one)
    const userReactedEmoji = currentUserReactions.size > 0 
      ? Array.from(currentUserReactions.keys())[0] 
      : null;

    // If user has reacted and it's not already in the base list, add it to the end
    if (userReactedEmoji && !baseReactions.includes(userReactedEmoji)) {
      return [...baseReactions, userReactedEmoji];
    }

    return baseReactions;
  }, [currentUserReactions]);

  // Add CSS styling to highlight selected emojis in the picker
  useEffect(() => {
    const highlightSelectedEmojis = () => {
      // Add a small delay to ensure the emoji picker has rendered
      setTimeout(() => {
        const emojiButtons = document.querySelectorAll('.smart-emoji-picker .EmojiPickerReact button');
        
        emojiButtons.forEach((button) => {
          const emojiSpan = button.querySelector('span');
          if (emojiSpan) {
            const emoji = emojiSpan.textContent;
            const isSelected = currentUserReactions.has(emoji);
            
            if (isSelected) {
              button.classList.add('user-selected-emoji');
              button.title = `Remove ${emoji} reaction`;
            } else {
              button.classList.remove('user-selected-emoji');
              button.title = `Add ${emoji} reaction`;
            }
          }
        });
      }, 100);
    };

    highlightSelectedEmojis();
    
    // Re-run when reactions change
    const interval = setInterval(highlightSelectedEmojis, 500);
    
    return () => clearInterval(interval);
  }, [currentUserReactions]);

  // Detect when the picker resizes (compact → expanded) and notify parent
  // so it can recalculate the picker's position. This replaces the old
  // approach of sniffing internal emoji-picker-react class names.
  useEffect(() => {
    const pickerElement = document.querySelector('.smart-emoji-picker');
    if (!pickerElement) return;

    let initialHeight = pickerElement.offsetHeight;
    const observer = new ResizeObserver(() => {
      const newHeight = pickerElement.offsetHeight;
      if (newHeight !== initialHeight) {
        initialHeight = newHeight;
        onExpand();
      }
    });

    observer.observe(pickerElement);
    return () => observer.disconnect();
  }, [onExpand]);

  return (
    <div className="smart-emoji-picker">
      {/* Emoji picker with smart highlighting */}
      <div className="emoji-picker-section">
        <EmojiPickerReact
          height={EMOJI_PICKER_DIMENSIONS.height}
          onEmojiClick={handleEmojiClick}
          previewConfig={{ showPreview: false }}
          lazyLoadEmojis={true}
          emojiStyle='native'
          reactionsDefaultOpen={true}
          allowExpandReactions={true}
          skinTonesDisabled={true}
          searchPlaceHolder={t('SmartEmojiPicker.searchPlaceholder')}
          reactions={quickReactions.map(emojiToUnified)}
          categories={[
            { category: Categories.SUGGESTED, name: t('SmartEmojiPicker.categoryFrequentlyUsed') },
            { category: Categories.SMILEYS_PEOPLE, name: t('SmartEmojiPicker.categorySmileys') },
            { category: Categories.ANIMALS_NATURE, name: t('SmartEmojiPicker.categoryAnimals') },
            { category: Categories.FOOD_DRINK, name: t('SmartEmojiPicker.categoryFood') },
            { category: Categories.TRAVEL_PLACES, name: t('SmartEmojiPicker.categoryTravel') },
            { category: Categories.ACTIVITIES, name: t('SmartEmojiPicker.categoryActivities') },
            { category: Categories.OBJECTS, name: t('SmartEmojiPicker.categoryObjects') },
            { category: Categories.SYMBOLS, name: t('SmartEmojiPicker.categorySymbols') },
            { category: Categories.FLAGS, name: t('SmartEmojiPicker.categoryFlags') },
          ]}
        />
      </div>
    </div>
  );
};

SmartEmojiPicker.propTypes = {
  messageId: PropTypes.string.isRequired,
  reactions: PropTypes.instanceOf(Map),
  onToggleReaction: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onExpand: PropTypes.func,
};

export default SmartEmojiPicker;